import { useCallback, useContext } from "react";

import { CartDrawerContext } from "@/providers/CartDrawerProvider";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  addToCart,
  clearCart,
  removeFromCart,
  updateQuantity,
} from "@/store/cart.slice";
import { CartItem } from "@/types";

type AddToCartInput = Omit<CartItem, "quantity">;
interface AddToCartOptions {
  openDrawer?: boolean;
}

export const useCart = () => {
  const cartDrawerContext = useContext(CartDrawerContext);
  const dispatch = useAppDispatch();
  const { items, serverCount, syncedLocalCount, total } = useAppSelector(
    (state) => state.cart,
  );
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const badgeCount =
    isAuthenticated && serverCount !== null
      ? Math.max(0, serverCount + (itemCount - syncedLocalCount))
      : itemCount;
  const hasItems = items.length > 0;
  const shippingFee = hasItems ? (total > 5_000_000 ? 0 : 30_000) : 0;
  const finalTotal = total + shippingFee;

  if (!cartDrawerContext) {
    throw new Error("useCart must be used within CartDrawerProvider.");
  }

  const {
    closeCartDrawer,
    isCartDrawerOpen,
    openCartDrawer,
    toggleCartDrawer,
  } = cartDrawerContext;

  const addItem = useCallback(
    (product: AddToCartInput, options?: AddToCartOptions) => {
      dispatch(addToCart(product));
      if (options?.openDrawer !== false) {
        openCartDrawer();
      }
    },
    [dispatch, openCartDrawer],
  );

  const removeItem = useCallback(
    (productId: string) => {
      dispatch(removeFromCart(productId));
    },
    [dispatch],
  );

  const updateItemQuantity = useCallback(
    (productId: string, quantity: number) => {
      dispatch(updateQuantity({ productId, quantity }));
    },
    [dispatch],
  );

  const clearCartItems = useCallback(() => {
    dispatch(clearCart());
  }, [dispatch]);

  return {
    items,
    total,
    badgeCount,
    itemCount,
    hasItems,
    shippingFee,
    finalTotal,
    isDrawerOpen: isCartDrawerOpen,
    addItem,
    removeItem,
    updateItemQuantity,
    clearCart: clearCartItems,
    openDrawer: openCartDrawer,
    closeDrawer: closeCartDrawer,
    toggleDrawer: toggleCartDrawer,
  };
};
